<template>
  <div
      ref="iconWrapper"
      class="GlobalIcon"
      :class="[...sizeClasses, colorClass]"
      aria-hidden="true"
  >
    <!-- SVG will be injected here -->
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, nextTick, computed } from 'vue';

const props = defineProps<{
  name: string
  size?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge'
  color?: keyof typeof colorMap
}>();

const iconWrapper = ref<HTMLDivElement | null>(null);

const colorMap = {
  base: 'text-icon-base',
  subtle: 'text-icon-subtle',
  strong: 'text-icon-strong',
  inactive: 'text-icon-inactive',
  inverse: 'text-icon-inverse',
  brand: 'text-icon-brand',
  success: 'text-icon-success',
  attention: 'text-icon-attention',
  warning: 'text-icon-warning',
  error: 'text-icon-error',
} as const;

const colorClass = computed(() => {
  return props.color && colorMap[props.color] ? colorMap[props.color] : '';
});

const sizeMap: Record<
    NonNullable<typeof props.size>,
    string[]
> = {
  xsmall: ['w-[12px]', 'h-[12px]', 'desk:w-[16px]', 'desk:h-[16px]'],
  small: ['w-[16px]', 'h-[16px]', 'desk:w-[20px]', 'desk:h-[20px]'],
  medium: ['w-[20px]', 'h-[20px]', 'desk:w-[24px]', 'desk:h-[24px]'],
  large: ['w-[24px]', 'h-[24px]', 'desk:w-[32px]', 'desk:h-[32px]'],
  xlarge: ['w-[32px]', 'h-[32px]', 'desk:w-[48px]', 'desk:h-[48px]'],
};

const sizeClasses = computed(() => sizeMap[props.size || 'medium']);

const injectSVG = async() => {
  if (!iconWrapper.value) return;

  try {
    const res = await fetch(`/icons/${props.name}.svg`);
    const raw = await res.text();

    const parser = new DOMParser();
    const doc = parser.parseFromString(raw, 'image/svg+xml');
    const svg = doc.querySelector('svg');

    if (svg) {
      svg.setAttribute('fill', 'currentColor');
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');

      svg.querySelectorAll('[fill]').forEach(el => el.removeAttribute('fill'));

      iconWrapper.value.innerHTML = '';
      iconWrapper.value.appendChild(svg);
    } else {
      console.warn(`No <svg> found in /icons/${props.name}.svg`);
    }
  } catch (e) {
    console.warn(`Failed to load icon: ${props.name}`, e);
  }
};

onMounted(() => nextTick(injectSVG));
watch(() => props.name, injectSVG);
</script>

<style scoped>
.GlobalIcon {
  display: inline-block;
  vertical-align: middle;
  line-height: 0;
}
</style>
